<template>
  <section class="admin-content" id="disease-detail">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between flex-wrap text-white" style="padding: 32px 0; gap: 12px;">
            <div class="d-flex align-items-center">
              <router-link to="/disease-tracking" class="header-icon-wrap mr-3 text-white"><i class="fe fe-arrow-left" style="font-size: 22px;"></i></router-link>
              <div>
                <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
                  <h4 class="mb-0 font-weight-bold">{{ s ? s.disease.name : "Disease" }}</h4>
                  <DiseaseCategoryChip v-if="s" :classification="s.classification" large show-source />
                </div>
                <small style="opacity: 0.8;" v-if="s">{{ s.diagnoses.map((d) => d.name + (d.diagnosis_code ? ` (${d.diagnosis_code})` : "")).join(" · ") }}{{ s.disease.is_chronic ? " · chronic" : "" }}</small>
              </div>
            </div>
            <PeriodPicker v-model="period" @change="load" default-preset="12m" />
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div v-if="loading && !s" class="card shadow-sm table-card"><div class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div></div>
          <div v-else-if="error" class="card shadow-sm table-card"><div class="empty-state"><i class="fe fe-alert-triangle"></i>{{ error }}</div></div>
          <template v-else-if="s">
            <div class="category-banner" :class="`category-banner--${s.classification.category}`">
              <DiseaseCategoryChip :classification="s.classification" />
              <div class="flex-grow-1 small">
                <b>{{ s.classification.description }}</b> {{ s.classification.reason }}
                <span v-if="s.classification.source === 'manual' && s.classification.automatic !== s.classification.category" class="text-muted">
                  (Set by hand; the data alone says {{ s.classification.automatic_label.toLowerCase() }}.)</span>
              </div>
              <div v-if="s.can_manage" class="d-flex align-items-center" style="gap: 6px;">
                <label for="category-select" class="small text-muted mb-0">Category</label>
                <select id="category-select" class="form-control form-control-sm" style="width: auto;" :value="s.disease.category || ''" :disabled="savingCategory" @change="setCategory($event.target.value)">
                  <option value="">Automatic (from the data)</option>
                  <option v-for="c in s.categories" :key="c.key" :value="c.key">{{ c.label }}</option>
                </select>
                <router-link :to="`/disease-tracking?edit=${id}`" class="btn btn-sm btn-outline-primary text-nowrap"><i class="fe fe-edit-2 mr-1"></i>Edit</router-link>
              </div>
            </div>

            <div class="alert" :class="alertClass" v-if="s.seasonality.this_month.level !== 'normal'">
              <i class="fe fe-alert-triangle mr-1"></i>
              <b>{{ s.seasonality.this_month.label }}:</b> {{ s.seasonality.this_month.so_far }} sufferers so far, on course for about
              {{ s.seasonality.this_month.projected }} against a usual {{ s.seasonality.this_month.expected }} for this month.
            </div>

            <div class="row">
              <div class="col-lg-3 col-md-6" v-for="tile in tiles" :key="tile.label">
                <div class="stat-card">
                  <div class="stat-icon-wrap" :style="{ background: tile.bg }"><i :class="tile.icon" :style="{ color: tile.color }"></i></div>
                  <div><p class="stat-label">{{ tile.label }}</p><h3 class="stat-value">{{ tile.value }}</h3><p class="stat-sub">{{ tile.sub }}</p></div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-8">
                <div class="card shadow-sm table-card mb-4">
                  <div class="form-card-header"><div class="section-number"><i class="fe fe-trending-up" style="font-size: 12px;"></i></div>
                    <h6 class="mb-0 font-weight-bold text-white">Sufferers and spend over time</h6></div>
                  <div class="card-body"><apexchart type="line" height="320" :options="trendOptions" :series="trendSeries" :key="'t' + k" /></div>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="card shadow-sm table-card mb-4">
                  <div class="form-card-header"><div class="section-number"><i class="fe fe-users" style="font-size: 12px;"></i></div>
                    <h6 class="mb-0 font-weight-bold text-white">By sex</h6></div>
                  <div class="card-body"><apexchart type="donut" height="320" :options="donutOptions(s.distribution.gender)" :series="s.distribution.gender.map((g) => g.count)" :key="'g' + k" /></div>
                </div>
              </div>
            </div>

            <DiseaseInsights :s="s" :k="k" />

            <div class="row">
              <div class="col-lg-4" v-for="chart in barCharts" :key="chart.title">
                <div class="card shadow-sm table-card mb-4">
                  <div class="form-card-header"><div class="section-number"><i :class="chart.icon" style="font-size: 12px;"></i></div>
                    <h6 class="mb-0 font-weight-bold text-white">{{ chart.title }}</h6></div>
                  <div class="card-body">
                    <apexchart v-if="chart.rows.length" type="bar" :height="Math.max(220, chart.rows.length * 30)" :options="barOptions(chart)" :series="[{ name: 'Sufferers', data: chart.rows.map((r) => r.count) }]" :key="chart.title + k" />
                    <div v-else class="empty-state">No data.</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-7">
                <div class="card shadow-sm table-card mb-4">
                  <div class="form-card-header"><div class="section-number"><i class="fe fe-calendar" style="font-size: 12px;"></i></div>
                    <div class="flex-grow-1"><h6 class="mb-0 font-weight-bold text-white">When in the year it strikes</h6>
                      <small style="color: rgba(255,255,255,.75);">Last 3 years · 1.0 = an average month{{ s.seasonality.danger_months.length ? ` · danger months: ${s.seasonality.danger_months.join(", ")}` : "" }}</small></div></div>
                  <div class="card-body">
                    <apexchart type="bar" height="240" :options="seasonOptions" :series="[{ name: 'Seasonal index', data: s.seasonality.months.map((m) => m.index || 0) }]" :key="'s' + k" />
                    <h6 class="small text-uppercase text-muted font-weight-bold mt-2 mb-0">Sufferers per month, last 36 months</h6>
                    <apexchart type="area" height="160" :options="historyOptions" :series="[{ name: 'Sufferers', data: s.seasonality.history.map((h) => h.sufferers) }]" :key="'h' + k" />
                  </div>
                </div>
              </div>
              <div class="col-lg-5">
                <div class="card shadow-sm table-card mb-4">
                  <div class="form-card-header"><div class="section-number"><i class="fe fe-git-merge" style="font-size: 12px;"></i></div>
                    <div class="flex-grow-1"><h6 class="mb-0 font-weight-bold text-white">Diagnosed alongside</h6>
                      <small style="color: rgba(255,255,255,.75);">Other diagnoses the same people had in the period</small></div></div>
                  <table class="table table-sm mb-0">
                    <thead class="table-head"><tr><th class="table-th">Diagnosis</th><th class="table-th text-right">Sufferers</th><th class="table-th text-right">Share</th></tr></thead>
                    <tbody>
                      <tr v-if="!s.related_diagnoses.length"><td colspan="3" class="empty-state">No other diagnoses recorded for these people.</td></tr>
                      <tr class="table-row" v-for="r in s.related_diagnoses" :key="r.id">
                        <td>{{ r.name }} <span class="small text-muted">{{ r.code }}</span></td><td class="text-right">{{ r.patients }}</td>
                        <td class="text-right"><div class="share-bar"><span :style="{ width: r.share_pct + '%' }"></span></div>{{ r.share_pct }}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <EnrolleeMap :endpoint="`/api/v1/auth/diseases/${id}/geography`" :extra-params="{ from: period.from, to: period.to }"
              :title="`Where ${s.disease.name} sufferers live`" subtitle="by the enrollee's address (dependants by their principal's)" unit="sufferers" :show-sectors="false" />

            <div class="card shadow-sm table-card mb-4">
              <div class="form-card-header flex-wrap">
                <div class="section-number"><i class="fe fe-list" style="font-size: 12px;"></i></div>
                <h6 class="mb-0 font-weight-bold text-white flex-grow-1">Sufferers</h6>
                <div class="btn-group btn-group-sm" role="group" aria-label="Which sufferers">
                  <button v-for="t in caseTypes" :key="t.key" type="button" class="btn" :class="caseType === t.key ? 'btn-light' : 'btn-outline-light'" @click="caseType = t.key; loadCases(1)">{{ t.label }}</button>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table mb-0">
                  <thead class="table-head"><tr><th class="table-th">Enrollee</th><th class="table-th">Sex / age</th><th class="table-th">Programme</th><th class="table-th">LGA</th><th class="table-th">Facility</th><th class="table-th">First case in period</th><th class="table-th">Previous case</th><th class="table-th text-right">Cases</th></tr></thead>
                  <tbody>
                    <tr v-if="!cases.length"><td colspan="8" class="empty-state">Nobody in this list for the period.</td></tr>
                    <tr class="table-row" v-for="c in cases" :key="c.type + c.id">
                      <td><router-link :to="c.type === 'dependant' ? `/dependent/${c.id}` : `/client/${c.id}`">{{ c.name }}</router-link>
                        <span v-if="c.is_new" class="sev sev-high ml-1">New</span><div class="small text-muted">{{ c.id_card_number }} · {{ c.type }}</div></td>
                      <td>{{ c.gender || "—" }} · {{ c.age === null ? "?" : c.age }}</td><td>{{ c.programme }}</td><td>{{ c.lga || "—" }}</td><td>{{ c.facility || "—" }}</td>
                      <td>{{ c.first_case_in_period }}</td><td>{{ c.previous_case || "—" }}</td><td class="text-right">{{ c.cases }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="card-footer bg-white d-flex justify-content-between align-items-center" v-if="casesMeta.last_page > 1">
                <small class="text-muted">Page {{ casesMeta.current_page }} of {{ casesMeta.last_page }} · {{ casesMeta.total }} sufferers</small>
                <div>
                  <button class="btn btn-sm btn-outline-secondary mr-1" :disabled="casesMeta.current_page <= 1" @click="loadCases(casesMeta.current_page - 1)">Previous</button>
                  <button class="btn btn-sm btn-outline-secondary" :disabled="casesMeta.current_page >= casesMeta.last_page" @click="loadCases(casesMeta.current_page + 1)">Next</button>
                </div>
              </div>
            </div>

            <div class="card shadow-sm table-card mb-4" v-if="s.alerts.length">
              <div class="form-card-header"><div class="section-number"><i class="fe fe-bell" style="font-size: 12px;"></i></div>
                <div class="flex-grow-1"><h6 class="mb-0 font-weight-bold text-white">Recent alerts</h6>
                  <small style="color: rgba(255,255,255,.75);">In-app (and the daily email) to {{ s.watchers.length ? s.watchers.map((w) => `${w.firstname} ${w.lastname}`.trim()).join(", ") : "the agency admins" }}</small></div></div>
              <div class="card-body">
                <div v-for="a in s.alerts" :key="a.id" class="small border-bottom py-1">
                  <span class="sev mr-1" :class="a.type === 'spike' ? 'sev-high' : 'sev-medium'">{{ a.type === "spike" ? "Spike" : "New" }}</span>{{ a.summary }}
                  <span class="text-muted"> · {{ a.created_at }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import PeriodPicker from "./components/PeriodPicker.vue";
import EnrolleeMap from "./components/EnrolleeMap.vue";
import DiseaseCategoryChip from "./components/DiseaseCategoryChip.vue";
import DiseaseInsights from "./components/DiseaseInsights.vue";

/** One tracked disease (GET diseases/{id}/summary, /cases, /geography). */
export default {
  components: { Navbar, PeriodPicker, EnrolleeMap, DiseaseCategoryChip, DiseaseInsights },
  data() {
    return {
      id: this.$route.params.id, period: { from: "", to: "" }, s: null, loading: false, error: "", k: 0, savingCategory: false,
      caseType: "new", cases: [], casesMeta: { current_page: 1, last_page: 1, total: 0 },
      caseTypes: [{ key: "new", label: "New" }, { key: "returning", label: "Returning" }, { key: "all", label: "All" }],
    };
  },
  computed: {
    money() { return (v) => "₦" + Math.round(v || 0).toLocaleString(); },
    alertClass() { return this.s.seasonality.this_month.level === "high" ? "alert-danger" : "alert-warning"; },
    tiles() {
      const k = this.s.kpis;
      return [
        { label: this.s.disease.is_chronic ? "Sufferers seen in period" : "Sufferers", value: k.sufferers.toLocaleString(), sub: k.per_1000_enrollees === null ? "" : `${k.per_1000_enrollees} per 1,000 enrollees`, icon: "fe fe-users", bg: "#eff6ff", color: "#2563eb" },
        { label: "New sufferers", value: k.new_sufferers.toLocaleString(), sub: `${k.returning_sufferers.toLocaleString()} returning${this.s.disease.is_chronic ? "" : ` (case in the ${this.s.disease.new_case_days} days before)`}`, icon: "fe fe-user-plus", bg: "#fef2f2", color: "#dc2626" },
        { label: "Spend (claimed)", value: this.money(k.amount_claimed), sub: `${this.money(k.amount_approved)} approved · ${k.claims} claims`, icon: "fe fe-credit-card", bg: "#fff7ed", color: "#ea580c" },
        k.registered_sufferers !== null
          ? { label: "Living with it", value: k.registered_sufferers.toLocaleString(), sub: "everyone ever diagnosed", icon: "fe fe-heart", bg: "#f5f3ff", color: "#7c3aed" }
          : { label: "Cost per sufferer", value: k.cost_per_sufferer === null ? "—" : this.money(k.cost_per_sufferer), sub: `${k.encounters} encounters`, icon: "fe fe-activity", bg: "#ecfdf5", color: "#059669" },
      ];
    },
    trendSeries() {
      const t = this.s.trend;
      const returning = t.sufferers.map((n, i) => n - t.new_sufferers[i]);
      return [
        { name: "New sufferers", type: "column", data: t.new_sufferers },
        { name: "Returning sufferers", type: "column", data: returning },
        { name: "Amount claimed", type: "line", data: t.amount_claimed },
      ];
    },
    trendOptions() {
      return {
        chart: { stacked: true, toolbar: { show: true } }, colors: ["#dc2626", "#93c5fd", "#ea580c"], stroke: { width: [0, 0, 3], curve: "smooth" },
        plotOptions: { bar: { columnWidth: "60%", borderRadius: 3 } }, xaxis: { categories: this.s.trend.categories, labels: { rotate: -45, hideOverlappingLabels: true } },
        yaxis: [{ seriesName: "New sufferers", title: { text: "Sufferers" } }, { seriesName: "New sufferers", show: false },
          { seriesName: "Amount claimed", opposite: true, title: { text: "₦ claimed" }, labels: { formatter: (v) => "₦" + Math.round(v).toLocaleString() } }],
        dataLabels: { enabled: true }, legend: { position: "top" }, tooltip: { shared: true, intersect: false },
      };
    },
    barCharts() {
      const d = this.s.distribution;
      return [
        { title: "By age", icon: "fe fe-bar-chart", rows: d.age.filter((r) => r.count || r.key !== "Unknown").map((r) => ({ label: r.key, count: r.count })), color: "#0891b2" },
        { title: "By programme", icon: "fe fe-layers", rows: d.programme.map((r) => ({ label: r.key, count: r.count })), color: "#7c3aed" },
        { title: "By facility", icon: "fe fe-home", rows: d.facility.map((r) => ({ label: r.name, count: r.count })), color: "#059669" },
      ];
    },
    seasonOptions() {
      const months = this.s.seasonality.months;
      return {
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: "65%", borderRadius: 3, distributed: true } },
        colors: months.map((m) => (m.danger ? "#dc2626" : (m.index || 0) >= 1 ? "#f59e0b" : "#93c5fd")),
        xaxis: { categories: months.map((m) => m.label) }, legend: { show: false }, dataLabels: { enabled: true },
        annotations: { yaxis: [{ y: 1, borderColor: "#64748b", label: { text: "average", style: { background: "#64748b", color: "#fff" } } }] },
        tooltip: { y: { formatter: (v, o) => `index ${v} · about ${months[o.dataPointIndex].average} sufferers` } },
      };
    },
    historyOptions() {
      return {
        chart: { toolbar: { show: false } }, colors: ["#dc2626"], stroke: { width: 2, curve: "smooth" },
        fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0.02 } }, dataLabels: { enabled: true },
        xaxis: { categories: this.s.seasonality.history.map((h) => h.label), labels: { show: false } },
      };
    },
  },
  mounted() { if (this.period.from) this.load(); },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      this.axios.get(`/api/v1/auth/diseases/${this.id}/summary`, { params: { from: this.period.from, to: this.period.to } })
        .then((res) => { this.s = res.data.data; this.k++; this.loadCases(1); })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load this disease."; })
        .finally(() => { this.loading = false; });
    },
    setCategory(category) {
      this.savingCategory = true;
      this.axios.put(`/api/v1/auth/diseases/${this.id}`, { category: category || null })
        .then(() => { this.$toasted.success(category ? "Category set." : "Category back to automatic.", { position: "top-center", duration: 2500 }); this.load(); })
        .catch((e) => this.$toasted.error((e.response && e.response.data && e.response.data.message) || "Could not change the category.", { position: "top-center", duration: 4000 }))
        .finally(() => { this.savingCategory = false; });
    },
    loadCases(page) {
      this.axios.get(`/api/v1/auth/diseases/${this.id}/cases`, { params: { from: this.period.from, to: this.period.to, type: this.caseType, page } })
        .then((res) => { this.cases = res.data.data; this.casesMeta = { current_page: res.data.current_page, last_page: res.data.last_page, total: res.data.total }; });
    },
    donutOptions(rows) {
      return { labels: rows.map((r) => r.key), colors: ["#2563eb", "#db2777", "#94a3b8", "#f59e0b"], legend: { position: "bottom" }, dataLabels: { enabled: true } };
    },
    barOptions(chart) {
      return {
        chart: { toolbar: { show: false } }, colors: [chart.color], plotOptions: { bar: { horizontal: true, barHeight: "70%", borderRadius: 3 } },
        xaxis: { categories: chart.rows.map((r) => r.label) }, dataLabels: { enabled: true },
      };
    },
  },
};
</script>

<style scoped>
@import "../reports/reports.css";
.share-bar { display: inline-block; width: 60px; height: 6px; background: #e2e8f0; border-radius: 3px; margin-right: 6px; vertical-align: middle; overflow: hidden; }
.share-bar span { display: block; height: 100%; background: #dc2626; }
.category-banner { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; background: #fff; border: 1px solid #e9ecef; border-left: 4px solid #94a3b8; border-radius: 12px; padding: 12px 16px; margin-bottom: 16px; box-shadow: 0 1px 4px rgba(0,0,0,0.05); }
.category-banner--sporadic { border-left-color: #14b8a6; }
.category-banner--endemic { border-left-color: #3b82f6; }
.category-banner--hyperendemic { border-left-color: #8b5cf6; }
.category-banner--outbreak { border-left-color: #f59e0b; }
.category-banner--epidemic { border-left-color: #ef4444; }
.category-banner--pandemic { border-left-color: #be123c; }
</style>
